<template>
    <v-container flat>
        <v-window v-model="step" touchless>
            <v-window-item :value="0">
                <UploadDocuments
                    :showFirstPrevious="false"
                    :documentType="'COMPANIES'"
                    :inExtensions="['csv', 'tsv', 'txt', 'xlsx', 'xls']"
                    :multiple="false"
                    :freeTag="false"
                    :gDrive="false"
                    :dBox="false"
                    @fileInput="onFileChanged"
                    @fileDeleted="onFileDeleted"
                    @testFileInput="onTestFileChanged"
                />
            </v-window-item>
            <v-window-item :value="1" style="max-width: 1000px">
                <ColumnCorrespondence
                    ref="columnComponent"
                    v-if="step > 0 && existingFile"
                    :result="result"
                    :extension="extension"
                    :actions="actions"
                    :ruleManager="ruleManager"
                    :formatColumns="formatColumns"
                    @columns-chosen="onChosenColumns($event)"
                    @back="step = 0"
                />
            </v-window-item>
            <v-window-item :value="2">
                <v-data-table
                    :headers="importedHeaders"
                    :items="importedItems"
                    class="table"
                    density="compact"
                    fixed-header
                    :loading="verifLoading"
                >
                    <template v-slot:item="props">
                        <tr
                            :style="
                                'background-color:' +
                                props.item.colorAlert +
                                '; pointer-events: none;'
                            "
                        >
                            <td class="text-center">{{ props.item.name }}</td>
                            <td class="text-center">{{ getSiretValue(props.item.siret) || "" }}</td>
                            <td class="text-center">{{ props.item.address }}</td>

                            <td
                                v-if="
                                    props.item.status === 'NEW_THIRDPARTY_COMPANY' ||
                                    props.item.status === 'NEW_CLIENT'
                                "
                                class="text-center"
                            >
                                <v-icon icon="$fa-check" color="green" />
                            </td>
                            <td
                                v-else-if="
                                    props.item.status === 'EXISTING_THIRDPARTY_COMPANY' ||
                                    props.item.status === 'EXISTING_CLIENT'
                                "
                                class="text-center"
                            >
                                La société existe déjà
                            </td>
                            <td
                                v-else-if="props.item.status === 'EXISTING_COMPANY'"
                                class="text-center"
                            >
                                <v-icon icon="$fa-check" color="green" />
                            </td>
                            <td v-else-if="props.item.status === 'SIRET_WRONG'" class="text-center">
                                Le siret n'est pas valable
                            </td>
                            <td v-else class="text-center">
                                <v-progress-circular indeterminate color="primary" />
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-window-item>
        </v-window>
    </v-container>
</template>

<script lang="ts">
import { Component, Prop, Watch, toNative } from "vue-facing-decorator";

import UploadDocuments from "../document/UploadDocuments.vue";
import ColumnCorrespondence from "./ColumnCorrespondence.vue";
import { ImportDocumentBase } from "./ImportDocumentBase";
import OssDataTable from "../ossComponents/ossDataTable2.vue";

import { DialogAction } from "../../services/dialogAction";

import CHECK_THIRDPARTY_COMPANIES from "../../graphql/company/CheckThirdPartyCompanies.gql";
import CHECK_ACCOUNTINGFIRM_CLIENTS from "../../graphql/company/CheckAccountingFirmClients.gql";

import IMPORT_THIRD_PARTY_COMPANIES from "../../graphql/company/ImportThirdPartyCompanies.gql";
import IMPORT_ACCOUNTINGFIRM_CLIENTS from "../../graphql/company/ImportAccountingFirmClients.gql";

@Component({
    components: {
        ColumnCorrespondence,
        OssDataTable,
        UploadDocuments,
    },
    emits: ["documentImported", "back", "width"],
})
export class CompaniesImport extends ImportDocumentBase {
    @Prop({ default: null }) actions: { action: DialogAction } | null;
    @Prop({ default: "" }) selectedCompanyId: string;
    @Prop({ default: "" }) accountingFirmId: string;

    step = 0;

    beforeMount() {
        this.setActionsButton();
    }

    async onChosenColumns(data) {
        const { importedItems, importedHeaders } = data;
        this.importedItems = importedItems;
        this.importedHeaders = importedHeaders;

        await this.verifyCompanies();
    }

    async verifyCompanies() {
        this.verifLoading = true;
        let result;
        switch (this.documentType) {
            case "CHILD_COMPANIES":
                if (this.selectedCompanyId && this.importedItems?.length) {
                    const query = await this.$apollo.query({
                        query: CHECK_THIRDPARTY_COMPANIES,
                        variables: {
                            thirdPartyCompanies: this.importedItems,
                            parentCompanyId: this.selectedCompanyId,
                        },
                    });
                    result =
                        query?.data?.checkThirdPartyCompanies?.thirdPartyCompanies || undefined;
                }
                break;
            case "CLIENT_COMPANIES":
                if (this.accountingFirmId && this.importedItems?.length) {
                    const query = await this.$apollo.query({
                        query: CHECK_ACCOUNTINGFIRM_CLIENTS,
                        variables: {
                            clientCompanies: this.importedItems,
                            accountingFirmId: this.accountingFirmId,
                        },
                    });
                    result = query?.data?.checkAccountingFirmClients?.clientCompanies || undefined;
                }
                break;
            default:
                result = undefined;
                break;
        }
        if (result) {
            this.importedItems = result;
            this.verifLoading = false;
            this.step++;
        }
        return result;
    }

    async valid() {
        // debugger
        this.importLoading = true;
        let result;
        switch (this.documentType) {
            case "CHILD_COMPANIES":
                if (this.selectedCompanyId && this.importedItems?.length) {
                    result = await this.$apollo.mutate({
                        mutation: IMPORT_THIRD_PARTY_COMPANIES,
                        variables: {
                            thirdPartyCompanies: this.importedItems,
                        },
                    });
                }
                break;
            case "CLIENT_COMPANIES":
                if (this.accountingFirmId && this.importedItems?.length) {
                    result = await this.$apollo.mutate({
                        mutation: IMPORT_ACCOUNTINGFIRM_CLIENTS,
                        variables: {
                            accountingFirmId: this.accountingFirmId,
                            clients: this.importedItems,
                        },
                    });
                }
                break;
            default:
                result = [];
                break;
        }

        if (result) {
            this.importLoading = false;
            this.$emit("documentImported");
        }
    }

    importDisabled() {
        if (!this.importedItems.length) {
            return true;
        }
        return false;
    }

    @Watch("step")
    setActionsButton() {
        if (this.actions && this.actions.action) {
            if (this.step === 0) {
                this.actions.action.display = true;
                this.actions.action.title = "Suivant";
                this.actions.action.disabled = () => {
                    return !this.existingFile;
                };
                this.actions.action.function = () => {
                    this.step++;
                };
                this.actions.action.previous = () => {
                    this.$emit("back");
                };
            } else if (this.step === 1) {
                this.actions.action.display = true;
                this.actions.action.title = "Suivant";
                if (this.$refs.columnComponent) {
                    //@ts-ignore
                    this.$refs.columnComponent.setActionButton();
                }
                //@ts-ignore
                this.actions.action.loading = () => {
                    return this.verifLoading;
                };
            } else if (this.step === 2) {
                this.actions.action.display = true;
                this.actions.action.title = "Importer";
                this.actions.action.disabled = () => {
                    return this.importDisabled();
                };
                this.actions.action.function = async () => {
                    await this.valid();
                };
                this.actions.action.previous = () => {
                    this.step--;
                };
                //@ts-ignore
                this.actions.action.loading = () => {
                    return this.importLoading;
                };
            }
        }
        this.$emit("width");
    }
}

export default toNative(CompaniesImport);
</script>
